import React from 'react';
import { KNOWLEDGE_BASE_ICONS, KnowledgeBaseIcon } from '../../schemas/knowledgeBase';
import * as Icons from 'lucide-react';
import clsx from 'clsx';

interface IconSelectorProps {
  value?: KnowledgeBaseIcon;
  onChange: (icon: KnowledgeBaseIcon) => void;
  disabled?: boolean;
}

export const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  // Helper function to get the icon component by name
  const getIconComponent = (
    iconName: KnowledgeBaseIcon
  ): React.ComponentType<{ size?: number; className?: string }> => {
    const IconComponent = Icons[iconName as keyof typeof Icons] as React.ComponentType<{
      size?: number;
      className?: string;
    }>;
    if (!IconComponent) {
      return Icons.Box; // Fallback icon
    }
    return IconComponent;
  };

  return (
    <div className='icon-selector' data-testid='kb-icon-select'>
      <div className='icon-grid grid grid-cols-7 gap-2'>
        {KNOWLEDGE_BASE_ICONS.map(icon => {
          const IconComponent = getIconComponent(icon);
          const isSelected = value === icon;

          return (
            <button
              key={icon}
              type='button'
              data-testid={`icon-select-${icon}`}
              data-selected={isSelected ? 'true' : undefined}
              onClick={() => !disabled && onChange(icon)}
              disabled={disabled}
              className={clsx(
                'icon-item relative flex items-center justify-center w-12 h-12 rounded-lg border-2 transition-all duration-200',
                'hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
                {
                  'border-gray-200 bg-white': !isSelected,
                  'border-blue-500 bg-blue-50': isSelected,
                  'opacity-50 cursor-not-allowed': disabled,
                  'cursor-pointer hover:border-blue-300': !disabled,
                }
              )}
              aria-label={`Select ${icon} icon`}
              aria-pressed={isSelected}
            >
              <IconComponent
                size={24}
                className={clsx('transition-colors duration-200', {
                  'text-gray-400': !isSelected,
                  'text-blue-500': isSelected,
                })}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default IconSelector;
